/**
 * HeatmapRackGrid Component
 * 
 * Heatmap visualization of rack locations showing activity intensity over time.
 * Based on LiveRackGrid but displays color-coded intensity instead of real-time animations.
 * 
 * Features:
 * - Color gradient from cold (blue) to hot (red) based on activity intensity
 * - Intensity score display on hover
 * - Activity breakdown tooltips showing event counts by type
 * - Support for multiple color schemes (Cold-Hot, Green-Red, Grayscale)
 * - Time window filtering (1h, 4h, 8h, Today)
 * - Automatic scrolling to target locations (for rack switches)
 * 
 * @author BizClick WMS
 * @version 1.29 - Added auto-scroll support for rack switches
 */

import React, { useMemo, useState, useRef, useCallback, useEffect } from 'react';
import { Package, AlertTriangle } from 'lucide-react';
import { BinLocation, RackStructure, compareSegments } from '../rack-builder/types';
import { ActivityTypeNames } from '../../../../types/locationActivity';
import { 
  LocationIntensity, 
  getHeatmapColor, 
  getHeatmapBorderColor, 
  getHeatmapTextColor 
} from './HeatmapCalculator';

interface HeatmapRackGridProps {
  /** Rack structure to display */
  rackStructure: RackStructure;
  
  /** Intensity data per location */
  locationIntensities: Map<string, LocationIntensity>;
  
  /** Optional: Target BinCode to scroll to (useful for rack switches) */
  targetBinCode?: string | null;
  
  /** Color scheme for heatmap */
  colorScheme?: 'cold-hot' | 'green-red' | 'grayscale';
  
  /** Optional: Show intensity values on cells */
  showIntensityValues?: boolean;
  
  /** Optional: Enable rack transition animation */
  enableTransition?: boolean;
  
  /** Optional: Enable automatic scrolling to target locations */
  enableAutoScroll?: boolean;
}

/**
 * Alternative color schemes
 */
const getColorForScheme = (
  intensity: number,
  scheme: 'cold-hot' | 'green-red' | 'grayscale'
): string => {
  if (scheme === 'grayscale') {
    // Grayscale for color-blind friendly display
    if (intensity === 0) return 'bg-gray-50';
    if (intensity <= 20) return 'bg-gray-200';
    if (intensity <= 40) return 'bg-gray-300';
    if (intensity <= 60) return 'bg-gray-400';
    if (intensity <= 80) return 'bg-gray-600';
    return 'bg-gray-800';
  }
  
  if (scheme === 'green-red') {
    // Green-Red for traffic light style
    if (intensity === 0) return 'bg-gray-100';
    if (intensity <= 20) return 'bg-green-200';
    if (intensity <= 40) return 'bg-green-300';
    if (intensity <= 60) return 'bg-yellow-300';
    if (intensity <= 80) return 'bg-orange-400';
    return 'bg-red-500';
  }
  
  // Default: cold-hot (blue to red)
  return getHeatmapColor(intensity);
};

/**
 * Get border color based on scheme
 */
const getBorderColorForScheme = (
  intensity: number,
  scheme: 'cold-hot' | 'green-red' | 'grayscale'
): string => {
  if (scheme === 'grayscale') {
    if (intensity === 0) return 'border-gray-300';
    if (intensity <= 40) return 'border-gray-400';
    if (intensity <= 80) return 'border-gray-600';
    return 'border-gray-800';
  }
  
  if (scheme === 'green-red') {
    if (intensity === 0) return 'border-gray-300';
    if (intensity <= 40) return 'border-green-500';
    if (intensity <= 60) return 'border-yellow-500';
    return 'border-red-600';
  }
  
  // Default: cold-hot
  return getHeatmapBorderColor(intensity);
};

export const HeatmapRackGrid: React.FC<HeatmapRackGridProps> = ({
  rackStructure,
  locationIntensities,
  targetBinCode = null,
  colorScheme = 'cold-hot',
  showIntensityValues = true,
  enableTransition = true,
  enableAutoScroll = true
}) => {
  const [hoveredCell, setHoveredCell] = useState<string | null>(null);
  const gridRef = useRef<HTMLDivElement>(null);
  const lastScrolledBinCode = useRef<string | null>(null);
  const pendingScrollTarget = useRef<string | null>(null);
  const scrollAttempts = useRef<number>(0);
  const previousRackNumber = useRef<string | null>(null);
  const isInitialMount = useRef(true);

  // Create a map for quick lookup: "vertical-shelf" -> BinLocation
  const locationMap = useMemo(() => {
    const map = new Map<string, BinLocation>();
    rackStructure.locations.forEach((loc: BinLocation) => {
      const key = `${loc.vertical}-${loc.shelf}`;
      map.set(key, loc);
    });
    return map;
  }, [rackStructure.locations]);

  // Sort shelves in DESCENDING order (highest shelf at top, lowest at bottom)
  const sortedShelves = useMemo(
    () => [...rackStructure.shelves].sort((a, b) => compareSegments(b, a)),
    [rackStructure.shelves]
  );

  // Sort verticals in ascending order (left to right: V01, V02, V03, ...)
  const sortedVerticals = useMemo(
    () => [...rackStructure.verticals].sort(compareSegments),
    [rackStructure.verticals]
  );

  /**
   * Scroll to a specific location cell with retry mechanism
   */
  const scrollToLocation = useCallback((binCode: string, force: boolean = false, retry: boolean = true) => {
    if (!enableAutoScroll || !gridRef.current) {
      return false;
    }

    // Avoid scrolling to the same location multiple times rapidly (unless forced)
    if (!force && lastScrolledBinCode.current === binCode) {
      return false;
    }

    // Find the cell element by data attribute
    const cellElement = gridRef.current.querySelector(`[data-bincode="${binCode}"]`);
    
    if (cellElement) {
      lastScrolledBinCode.current = binCode;
      pendingScrollTarget.current = null;
      scrollAttempts.current = 0;
      
      console.log(`[HeatmapRackGrid] Scrolling to ${binCode}`);
      
      // Scroll with smooth behavior and center alignment
      cellElement.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'center'
      });

      // Reset the last scrolled tracker after a delay to allow new scrolls
      setTimeout(() => {
        if (lastScrolledBinCode.current === binCode) {
          lastScrolledBinCode.current = null;
        }
      }, 2000);
      
      return true;
    } else {
      console.log(`[HeatmapRackGrid] Cell not found for ${binCode}, attempt ${scrollAttempts.current}`);
      
      // If retry enabled and we haven't tried too many times, set pending target
      if (retry && scrollAttempts.current < 10) {
        pendingScrollTarget.current = binCode;
        scrollAttempts.current++;
        return false;
      }
      
      return false;
    }
  }, [enableAutoScroll]);

  /**
   * Handle pending scroll attempts - retry when DOM is ready
   */
  useEffect(() => {
    if (pendingScrollTarget.current && scrollAttempts.current < 10) {
      const targetBin = pendingScrollTarget.current;
      const timeout = setTimeout(() => {
        const success = scrollToLocation(targetBin, true, true);
        if (!success && scrollAttempts.current < 10) {
          console.log(`[HeatmapRackGrid] Retry scroll to ${targetBin} in 100ms...`);
        } else if (success) {
          console.log(`[HeatmapRackGrid] Successfully scrolled to ${targetBin} after ${scrollAttempts.current} attempts`);
        } else {
          console.warn(`[HeatmapRackGrid] Failed to scroll to ${targetBin} after ${scrollAttempts.current} attempts`);
          pendingScrollTarget.current = null;
          scrollAttempts.current = 0;
        }
      }, 100);
      
      return () => clearTimeout(timeout);
    }
  }, [pendingScrollTarget.current, scrollAttempts.current, rackStructure.locations, scrollToLocation]);

  /**
   * Handle target BinCode prop changes (for rack switches)
   */
  useEffect(() => {
    if (targetBinCode && enableAutoScroll) {
      console.log(`[HeatmapRackGrid] Target BinCode received: ${targetBinCode}`);
      
      // Check if rack has changed
      const hasRackChanged = previousRackNumber.current !== rackStructure.rackNumber;
      
      if (hasRackChanged) {
        console.log(`[HeatmapRackGrid] Rack changed from ${previousRackNumber.current} to ${rackStructure.rackNumber}`);
        previousRackNumber.current = rackStructure.rackNumber;
        
        // Set pending target and wait for DOM
        pendingScrollTarget.current = targetBinCode;
        scrollAttempts.current = 0;
        
        // Initial attempt after rack rendering delay
        setTimeout(() => {
          scrollToLocation(targetBinCode, true, true);
        }, 450);
      } else {
        // Same rack, scroll immediately
        setTimeout(() => {
          scrollToLocation(targetBinCode, false, true);
        }, 100);
      }
    }
  }, [targetBinCode, rackStructure.rackNumber, enableAutoScroll, scrollToLocation]);

  /**
   * Initialize rack tracking on mount
   */
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      previousRackNumber.current = rackStructure.rackNumber;
    }
  }, []);

  /**
   * Get location by grid position
   */
  const getLocationAt = (vertical: string, shelf: string): BinLocation | undefined => {
    const key = `${vertical}-${shelf}`;
    return locationMap.get(key);
  };

  /**
   * Get cell class names with heatmap colors
   */
  const getCellClassName = (location: BinLocation | undefined): string => {
    if (!location) {
      return 'bg-gray-50 border border-dashed border-gray-200';
    }

    const intensity = locationIntensities.get(location.binCode);
    const intensityValue = intensity?.intensity || 0;
    
    const baseClasses = 'relative border transition-all duration-300 cursor-pointer';
    const colorClass = getColorForScheme(intensityValue, colorScheme);
    const borderClass = getBorderColorForScheme(intensityValue, colorScheme);
    const hoverClass = hoveredCell === location.binCode ? 'ring-2 ring-blue-500 ring-offset-1' : '';

    return `${baseClasses} ${colorClass} ${borderClass} ${hoverClass}`;
  };

  /**
   * Get text color for intensity
   */
  const getTextColorClass = (location: BinLocation): string => {
    const intensity = locationIntensities.get(location.binCode);
    const intensityValue = intensity?.intensity || 0;
    
    // Override for grayscale scheme
    if (colorScheme === 'grayscale') {
      return intensityValue > 60 ? 'text-white' : 'text-gray-800';
    }
    
    return getHeatmapTextColor(intensityValue);
  };

  /**
   * Render intensity overlay
   */
  const renderIntensityOverlay = (location: BinLocation) => {
    const intensity = locationIntensities.get(location.binCode);
    
    if (!intensity || !showIntensityValues) {
      return null;
    }

    return (
      <div className="absolute top-1 right-1 bg-white/90 rounded px-1.5 py-0.5 text-xs font-bold shadow-sm">
        {intensity.intensity.toFixed(0)}%
      </div>
    );
  };

  /**
   * Render tooltip with activity breakdown
   */
  const renderTooltip = (location: BinLocation) => {
    if (hoveredCell !== location.binCode) {
      return null;
    }

    const intensity = locationIntensities.get(location.binCode);
    
    if (!intensity || intensity.totalEvents === 0) {
      return (
        <div className="absolute z-50 left-1/2 -translate-x-1/2 top-full mt-2 bg-gray-900 text-white text-xs rounded-lg px-3 py-2 shadow-xl whitespace-nowrap pointer-events-none">
          <div className="font-semibold">{location.binCode}</div>
          <div className="text-gray-300 mt-1">Ingen aktivitet</div>
        </div>
      );
    }

    return (
      <div className="absolute z-50 left-1/2 -translate-x-1/2 top-full mt-2 bg-gray-900 text-white text-xs rounded-lg px-3 py-2 shadow-xl whitespace-nowrap pointer-events-none max-w-xs">
        <div className="font-semibold mb-1">{location.binCode}</div>
        <div className="text-blue-300 font-medium">
          Intensitet: {intensity.intensity.toFixed(1)}% ({intensity.totalEvents} events)
        </div>
        
        {/* Activity breakdown */}
        <div className="mt-2 space-y-0.5 border-t border-gray-700 pt-1.5">
          <div className="text-gray-400 text-[10px] font-semibold mb-1">AKTIVITETSFORDELING:</div>
          {Object.entries(intensity.activityBreakdown).map(([type, count]) => {
            const activityType = Number(type);
            const activityName = ActivityTypeNames[activityType as keyof typeof ActivityTypeNames];
            return (
              <div key={type} className="flex justify-between items-center text-[10px]">
                <span className="text-gray-300">{activityName}:</span>
                <span className="text-white font-medium ml-2">{count}</span>
              </div>
            );
          })}
        </div>
        
        {/* Weighted count */}
        <div className="mt-2 pt-1.5 border-t border-gray-700 text-[10px] text-gray-400">
          Vektet score: {intensity.weightedEventCount.toFixed(1)}
        </div>
      </div>
    );
  };

  if (rackStructure.locations.length === 0) {
    return (
      <div className="flex items-center justify-center h-64 text-gray-500">
        <p>Ingen lokasjoner å vise</p>
      </div>
    );
  }

  return (
    <div 
      className={`w-full h-full flex flex-col ${enableTransition ? 'rack-fade-in' : ''}`}
    >
      {/* Grid container - fills all available space */}
      <div
        ref={gridRef}
        className="w-full h-full border border-gray-300 bg-white rounded-lg p-4 overflow-auto"
        style={{ userSelect: 'none' }}
      >
        {/* Header with vertical numbers - sticky */}
        <div className="flex mb-3 sticky top-0 bg-white z-10 border-b border-gray-200 pb-1">
          <div className="w-20 flex-shrink-0" /> {/* Spacer for shelf labels */}
          {sortedVerticals.map(vertical => (
            <div
              key={vertical}
              className="w-36 text-center font-semibold text-base text-gray-700 flex-shrink-0"
            >
              V{/^\d+$/.test(vertical) ? vertical.padStart(2, '0') : vertical}
            </div>
          ))}
        </div>

        {/* Rack visualization */}
        <div className="space-y-1.5">
          {sortedShelves.map(shelf => (
            <div key={shelf} className="flex items-center">
              {/* Shelf label - sticky */}
              <div className="w-20 text-right pr-4 font-semibold text-base text-gray-700 flex-shrink-0 sticky left-0 bg-white z-5">
                H{/^\d+$/.test(shelf) ? shelf.padStart(2, '0') : shelf}
              </div>

              {/* Cells for this shelf */}
              {sortedVerticals.map(vertical => {
                const location = getLocationAt(vertical, shelf);
                const cellClass = getCellClassName(location);
                const textColorClass = location ? getTextColorClass(location) : 'text-gray-400';

                return (
                  <div
                    key={`${vertical}-${shelf}`}
                    data-bincode={location?.binCode}
                    className={`w-36 h-30 ${cellClass} flex flex-col items-center justify-center p-2 text-sm flex-shrink-0`}
                    onMouseEnter={() => location && setHoveredCell(location.binCode)}
                    onMouseLeave={() => setHoveredCell(null)}
                  >
                    {location ? (
                      <>
                        {/* Intensity overlay */}
                        {renderIntensityOverlay(location)}

                        {/* BinCode */}
                        <div className={`font-mono font-semibold text-sm ${textColorClass} relative z-10`}>
                          {location.binCode}
                        </div>

                        {/* Location indicators - fixed height */}
                        <div className="flex items-center justify-center space-x-1.5 mt-1.5 h-6 relative z-10">
                          {/* Dedicated location (has ItemNo) */}
                          {location.itemNo && (
                            <div title="Dedikert lokasjon">
                              <Package className={`w-4 h-4 ${textColorClass.replace('text-', 'text-green-').replace('800', '600').replace('white', 'green-200')}`} />
                            </div>
                          )}

                          {/* Low priority indicator */}
                          {location.pickPriorityNo && location.pickPriorityNo > 50 && (
                            <div title="Lav prioritet">
                              <AlertTriangle className={`w-4 h-4 ${textColorClass.replace('text-', 'text-yellow-').replace('800', '600').replace('white', 'yellow-200')}`} />
                            </div>
                          )}
                        </div>

                        {/* Size/Type info */}
                        <div className={`text-xs mt-1 relative z-10 ${textColorClass} opacity-75`}>
                          {location.binSizeNo}/{location.binTypeNo}
                        </div>

                        {/* Tooltip */}
                        {renderTooltip(location)}
                      </>
                    ) : (
                      <div className="text-gray-400 text-[8px]">-</div>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>

      {/* Compact legend */}
      <div className="mt-2 flex items-center justify-between text-xs">
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-1.5">
            <div className={`w-3 h-3 ${getColorForScheme(10, colorScheme)} border border-gray-400 rounded`} />
            <span>Lav</span>
          </div>
          <div className="flex items-center space-x-1.5">
            <div className={`w-3 h-3 ${getColorForScheme(50, colorScheme)} border border-gray-500 rounded`} />
            <span>Moderat</span>
          </div>
          <div className="flex items-center space-x-1.5">
            <div className={`w-3 h-3 ${getColorForScheme(90, colorScheme)} border border-gray-600 rounded`} />
            <span>Høy</span>
          </div>
        </div>
        <div className="text-gray-500">
          🔥 Aktivitetsintensitet (0-100%)
        </div>
      </div>
    </div>
  );
};

export default HeatmapRackGrid;
